import Controller from '@ember/controller';
import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import ContactModel from 'donor-elf-web/models/contact';
import { type GroupIndexRouteModel } from 'donor-elf-web/routes/funds/fund/groups/group/index';
import AbilitiesService from 'donor-elf-web/services/abilities';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import Settings from 'donor-elf-web/services/settings';
import FileSaver from 'file-saver';
import Papa from 'papaparse';

export default class GroupIndexController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare router: RouterService;
  @service declare settings: Settings;

  model!: GroupIndexRouteModel;

  @tracked q: string = '';

  get filterApplied() {
    return this.q.length > 2;
  }

  get sortedContacts() {
    return sortByProperties(this.model.contacts, ['name:asc']);
  }

  get filteredContacts() {
    let { sortedContacts } = this;
    let q = this.q.trim();
    if (q.length < 2) {
      return sortedContacts;
    }
    let matchingContactIds: any = {};
    this.contactsCache.search(q).forEach((contact) => {
      matchingContactIds[contact.id] = true;
    });
    return sortedContacts.filter((contact) => {
      return matchingContactIds[contact.id];
    });
  }

  @action
  templateDidInsert() {
    this.contactsCache.nextPreviousContactIds = this.sortedContacts.map(
      (x) => x.id,
    );
  }

  @action
  templateWillDestroy() {
    this.q = '';
  }

  get actionMenuItems() {
    let menuItems: any[] = [
      {
        label: 'Add to Group',
        svgJar: 'plus',
        route: 'funds.fund.groups.group.add',
      },
      {
        label: 'Edit Group Name',
        svgJar: 'edit',
        route: 'funds.fund.groups.group.edit-name',
      },
      {
        label: 'Delete Group',
        svgJar: 'trash',
        route: 'funds.fund.groups.group.delete',
      },
    ];
    if (this.settings.hasMailchimpFundSync) {
      menuItems.push({
        label: 'Sync with MailChimp',
        svgJar: 'plus',
        onClick: () => {
          this.router.transitionTo('funds.fund.groups.group.add-to-mailchimp');
        },
      });
    }
    menuItems.push({
      label: 'Download',
      svgJar: 'download',
      onClick: () => {
        this.download();
      },
    });
    return menuItems;
  }

  @action
  updateFilter(e: any) {
    this.q = e.target.value.trim();
  }

  @action
  handleRemovedFromGroup(contact: ContactModel) {
    const index = this.model.contacts.indexOf(contact);

    if (index !== -1) {
      this.model.contacts.splice(index, 1);
    }
  }

  @action
  async download() {
    let rows = [];
    let headerRow: any[] = [];
    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      this.contactsCache.contacts,
    );
    rows.push(headerRow);
    for (let contact of this.model.contacts) {
      let row: any[] = [];
      await this.contactsExport.addContactInfoToRow(row, contact, headerInfo);
      rows.push(row);
    }
    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, `${this.model.group}-contacts.csv`, true);
  }
}
